<script setup>
import { Head, Link, useForm } from '@inertiajs/vue3';
import { ref } from 'vue';
import QuickContactWidget from '@/FrontComponents/QuickContactWidget.vue';

defineProps({
    title: {
        type: String,
        default: 'OlAvtoStore — Автозапчастини'
    }
});

const form = useForm({
    search: '',
});

const showMenu = ref(false);

function submitSearch() {
    if (form.search && form.search.trim() !== '') {
        form.get('/catalog', { preserveScroll: true });
    }
}
</script>

<template>
    <Head :title="title" />

    <div class="min-h-screen flex flex-col bg-slate-50 text-slate-800 antialiased selection:bg-teal-500 selection:text-white">
        <!-- Top Banner / Emergency Contacts -->
        <div class="bg-slate-900 text-slate-300 text-xs py-2 px-4 border-b border-slate-800">
            <div class="container mx-auto flex flex-col sm:flex-row justify-between items-center gap-2">
                <div class="flex items-center gap-4">
                    <span class="inline-flex items-center gap-1.5 font-medium text-emerald-400">
                        <span class="relative flex h-2 w-2">
                          <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                          <span class="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                        </span>
                        Магазин працює у звичайному режимі
                    </span>
                    <span class="hidden md:inline text-slate-600">|</span>
                    <span class="hidden md:inline text-slate-400">Швидка доставка по Україні 1-3 дні</span>
                </div>
                <div class="flex items-center gap-4 text-slate-300">
                    <a href="tel:+380501356035" class="hover:text-teal-400 transition-colors flex items-center gap-1">
                        <svg class="w-3.5 h-3.5 text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg>
                        +38 (050) 135-60-35
                    </a>
                </div>
            </div>
        </div>

        <!-- Main Sticky Navbar -->
        <header class="bg-white/90 backdrop-blur-md border-b border-slate-200/80 sticky top-0 z-50 transition-all shadow-xs">
            <div class="container mx-auto px-4 sm:px-6 flex justify-between items-center h-20">
                <!-- Logo -->
                <Link href="/" class="flex items-center gap-3 group">
                    <div class="w-11 h-11 rounded-2xl bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center text-white font-black text-xl shadow-lg shadow-teal-500/20 group-hover:scale-105 group-hover:rotate-1 transition-all duration-300">
                        OA
                    </div>
                    <div class="flex flex-col">
                        <span class="text-2xl font-black tracking-tight text-slate-900 leading-none">
                            OlAvto<span class="text-teal-600">Store</span>
                        </span>
                        <span class="text-[10px] font-semibold tracking-wider text-slate-400 uppercase mt-0.5">Автозапчастини & Аксесуари</span>
                    </div>
                </Link>

                <!-- Desktop Navigation Links -->
                <nav class="hidden md:flex items-center space-x-8 text-sm font-semibold text-slate-700">
                    <Link href="/" class="hover:text-teal-600 transition-colors py-2 relative group">
                        Головна
                        <span class="absolute bottom-0 left-0 w-0 h-0.5 bg-teal-600 group-hover:w-full transition-all duration-300"></span>
                    </Link>
                    <Link href="/catalog" class="hover:text-teal-600 transition-colors py-2 relative group">
                        Каталог запчастин
                        <span class="absolute bottom-0 left-0 w-0 h-0.5 bg-teal-600 group-hover:w-full transition-all duration-300"></span>
                    </Link>
                </nav>

                <!-- Search Form & Actions -->
                <div class="hidden lg:flex items-center space-x-4">
                    <form @submit.prevent="submitSearch" class="relative">
                        <input 
                            v-model="form.search" 
                            type="text" 
                            placeholder="Пошук за назвою або OE кодом..." 
                            class="w-72 py-2.5 pl-4 pr-10 rounded-full bg-slate-100/80 border border-slate-200 text-xs font-medium text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:bg-white focus:border-transparent transition-all shadow-xs"
                        >
                        <button type="submit" aria-label="Знайти" class="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-teal-600 transition-colors">
                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>
                        </button>
                    </form>
                </div>

                <!-- Mobile menu toggle button -->
                <button @click="showMenu = !showMenu" class="md:hidden p-2.5 rounded-xl text-slate-700 hover:bg-slate-100 transition-colors focus:outline-none">
                    <svg v-if="!showMenu" class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"/></svg>
                    <svg v-else class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
                </button>
            </div>

            <!-- Mobile menu drawer -->
            <transition enter-active-class="transition duration-200 ease-out" enter-from-class="opacity-0 -translate-y-2" enter-to-class="opacity-100 translate-y-0" leave-active-class="transition duration-150 ease-in" leave-from-class="opacity-100 translate-y-0" leave-to-class="opacity-0 -translate-y-2">
                <div v-show="showMenu" class="md:hidden border-t border-slate-200 bg-white/95 backdrop-blur-md p-4 space-y-3">
                    <Link href="/" @click="showMenu = false" class="block px-4 py-2.5 rounded-xl font-semibold text-slate-700 hover:bg-teal-50 hover:text-teal-600 transition-colors">
                        Головна
                    </Link>
                    <Link href="/catalog" @click="showMenu = false" class="block px-4 py-2.5 rounded-xl font-semibold text-slate-700 hover:bg-teal-50 hover:text-teal-600 transition-colors">
                        Каталог запчастин
                    </Link>
                    
                    <div class="pt-2">
                        <form @submit.prevent="() => { submitSearch(); showMenu = false; }" class="relative">
                            <input 
                                v-model="form.search" 
                                type="text" 
                                placeholder="Пошук запчастин..." 
                                class="w-full py-2.5 pl-4 pr-10 rounded-xl bg-slate-100 border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                            >
                            <button type="submit" class="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>
                            </button>
                        </form>
                    </div>
                </div>
            </transition>
        </header>

        <!-- Main Slot Content -->
        <div class="flex-grow">
            <slot />
        </div>

        <!-- Footer -->
        <footer class="bg-slate-950 text-slate-400 py-16 border-t border-slate-800 mt-20">
            <div class="container mx-auto px-4 sm:px-6 grid grid-cols-1 md:grid-cols-4 gap-10">
                <div class="space-y-4">
                    <div class="flex items-center gap-3">
                        <div class="w-9 h-9 rounded-xl bg-teal-600 flex items-center justify-center text-white font-black text-lg">
                            OA
                        </div>
                        <span class="text-xl font-black text-white tracking-tight">OlAvtoStore</span>
                    </div>
                    <p class="text-xs leading-relaxed text-slate-400">
                        Ваш надійний інтернет-магазин оригінальних автозапчастин та якісних аналогів. Офіційна гарантія та оперативна доставка.
                    </p>
                </div>

                <div>
                    <h4 class="text-white font-bold text-sm uppercase tracking-wider mb-4">Навігація</h4>
                    <ul class="space-y-2.5 text-xs font-medium">
                        <li><Link href="/" class="hover:text-white transition-colors">Головна сторінка</Link></li>
                        <li><Link href="/catalog" class="hover:text-white transition-colors">Каталог автозапчастин</Link></li>
                    </ul>
                </div>

                <div>
                    <h4 class="text-white font-bold text-sm uppercase tracking-wider mb-4">Контакти відділу продажів</h4>
                    <div class="space-y-2 text-xs">
                        <a href="tel:+380501356035" class="flex items-center gap-2 text-slate-300 hover:text-teal-400 transition-colors font-medium">
                            <svg class="w-4 h-4 text-teal-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg>
                            +38 (050) 135-60-35
                        </a>
                    </div>
                </div>

                <div>
                    <h4 class="text-white font-bold text-sm uppercase tracking-wider mb-4">Способи оплати</h4>
                    <p class="text-xs text-slate-400 mb-3">Приймаємо зручні для вас види розрахунку:</p>
                    <div class="flex flex-wrap gap-2">
                        <span class="px-3 py-1.5 bg-slate-900 border border-slate-800 rounded-lg text-xs font-semibold text-slate-300">Visa / MasterCard</span>
                        <span class="px-3 py-1.5 bg-slate-900 border border-slate-800 rounded-lg text-xs font-semibold text-slate-300">Післяплата</span>
                    </div>
                </div>
            </div>

            <div class="container mx-auto px-4 sm:px-6 mt-12 pt-6 border-t border-slate-800/80 text-center text-xs text-slate-500">
                &copy; {{ new Date().getFullYear() }} OlAvtoStore. Всі права захищені.
            </div>
        </footer>
        <!-- Quick Floating Contact Widget -->
        <QuickContactWidget />
    </div>
</template>

<style scoped>
</style>
